import {
  UniversalUpgradeProductCard,
  UpgradesSelector,
  UpgradesSelectorInputs,
  useProducts,
} from '@airship-platform/airship-proposal';
import {
  EmptyState,
  FlexBox,
  Loader,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { TrashIcon } from '@airship-platform/jam-web/icons';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Link, useNavigate } from 'react-router';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals/AppointmentProposalsProvider';
import { useAppointmentProposalUpgrades } from '../../../data-access/appointmentProposalUpgrades';
import { useAppointments } from '../../../data-access/appointments';
import { usePricebookUniversalUpgradeProducts } from '../../../data-access/pricebook-universal-upgrades-product';
import { SectionWrapper } from '../../../patterns';

const FORM_ID = 'upgradesForm';

const UpgradesPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    pricebookUniversalUpgradeProducts,
    isLoading: isUniversalUpgradesLoading,
    isLoaded: isUniversalUpgradesLoaded,
  } = usePricebookUniversalUpgradeProducts();

  const {
    proposalUpgrades,
    isLoading: isProposalUpgradesLoading,
    isLoaded: isProposalUpgradesLoaded,
    deleteAppointmentProposalUpgrade,
  } = useAppointmentProposalUpgrades();

  const {
    currentProposal,
    isLoading: isProposalLoading,
    updateAppointmentProposal,
  } = useAppointmentProposals();

  const { currentAppointment } = useAppointments();
  const { updateProductsFilter } = useProducts();

  if (
    (isUniversalUpgradesLoading || isProposalUpgradesLoading) &&
    !(isUniversalUpgradesLoaded && isProposalUpgradesLoaded)
  ) {
    return <Loader isLoading message="Loading Upgrades" />;
  }

  if (!pricebookUniversalUpgradeProducts.length || !currentProposal) {
    return (
      <EmptyState
        title="No upgrades found"
        subtitle="Please add upgrades to the appointment proposal."
      />
    );
  }

  const selectedUpgradeIds =
    currentProposal.selectedUpgradeIds ??
    pricebookUniversalUpgradeProducts.map(
      (upgrade) => upgrade.pricebookUniversalUpgradeId
    );

  const onSubmit = (data: UpgradesSelectorInputs) => {
    updateAppointmentProposal(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      {
        selectedUpgradeIds: data.selectedUpgradeIds,
        proposalType: data.proposalType,
        installationType: data.installationType,
      }
    ).then(() => navigate('../service-menu'));
  };

  const handleAddUpgrades = () => {
    updateProductsFilter(
      {
        sortOrder: 'desc',
        pricebookTypes: ['EQUIPMENT', 'SERVICE'],
        categoryIds: [],
        names: [],
        businessUnitIds: currentAppointment?.job.businessUnitId
          ? [currentAppointment.job.businessUnitId]
          : [],
      },
      { reset: true }
    );
    navigate('create');
  };

  return (
    <SectionWrapper
      maxWidth={'md'}
      width={'100%'}
      header="Edit upgrades"
      subheader="Select the best upgrades for your customer."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../service-menu',
          label: 'Cancel',
        },
        right: {
          type: 'submit',
          component: LoadingButton,
          form: FORM_ID,
          label: 'Save',
          loading: isUniversalUpgradesLoading || isProposalLoading,
          disabled: isUniversalUpgradesLoading || isProposalLoading,
        },
      }}
    >
      <FlexBox flexDirection={'column'} gap={2} width="100%" mb={6}>
        <FlexBox justifyContent="flex-end">
          <Button
            color="softGold"
            onClick={handleAddUpgrades}
            disabled={isProposalUpgradesLoading}
          >
            Add Upgrade
          </Button>
        </FlexBox>
        <FlexBox flexDirection={'column'} gap={2}>
          {proposalUpgrades.map((upgrade) => (
            <FlexBox key={upgrade.pricebookId} justifyContent="space-between">
              <UniversalUpgradeProductCard upgrade={upgrade} />
              <FlexBox alignItems="center" gap={2}>
                <IconButton
                  color="softGold"
                  disabled={isProposalUpgradesLoading}
                  onClick={() =>
                    deleteAppointmentProposalUpgrade(
                      upgrade.appointmentId,
                      upgrade.appointmentProposalId,
                      upgrade.appointmentProposalUpgradeId
                    )
                  }
                >
                  <TrashIcon />
                </IconButton>
              </FlexBox>
            </FlexBox>
          ))}
        </FlexBox>
        <UpgradesSelector
          formId={FORM_ID}
          defaultValues={{ ...currentProposal, selectedUpgradeIds }}
          upgrades={pricebookUniversalUpgradeProducts}
          isLoading={isUniversalUpgradesLoading || isProposalLoading}
          onSubmit={onSubmit}
        />
      </FlexBox>
    </SectionWrapper>
  );
};

export default UpgradesPage;
